import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import client from '../api/client';

const useExerciseStore = create(
  persist(
    (set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  sections: [],
  exercises: [],
  current: null,
  types: [],
  prompts: [],
  complexityLevels: [],

  // ── UI preferences (persisted) ─────────────────────────────────────────────
  teacherSelectedTypeId: null,
  setTeacherSelectedTypeId: (id) => set({ teacherSelectedTypeId: id }),

  // ── Complexity Levels ──────────────────────────────────────────────────────

  fetchComplexityLevels: async () => {
    const { data } = await client.get('/complexity-levels/');
    set({ complexityLevels: data });
    return data;
  },

  // ── Sections ───────────────────────────────────────────────────────────────

  fetchSections: async () => {
    const { data } = await client.get('/sections');
    set({ sections: data });
  },

  createSection: async (payload) => {
    const { data } = await client.post('/sections', payload);
    set({ sections: [...get().sections, data] });
    return data;
  },

  updateSection: async (id, payload) => {
    const { data } = await client.put(`/sections/${id}`, payload);
    set({ sections: get().sections.map((s) => (s.id === id ? data : s)) });
    return data;
  },

  deleteSection: async (id) => {
    await client.delete(`/sections/${id}`);
    set({ sections: get().sections.filter((s) => s.id !== id) });
  },

  // ── Exercise Types ─────────────────────────────────────────────────────────

  fetchTypes: async () => {
    const { data } = await client.get('/exercises/types');
    set({ types: data });
  },

  createExerciseType: async (payload) => {
    const { data } = await client.post('/exercises/types', payload);
    set({ types: [...get().types, data] });
    return data;
  },

  updateExerciseType: async (id, payload) => {
    const { data } = await client.put(`/exercises/types/${id}`, payload);
    set({ types: get().types.map((t) => (t.id === id ? data : t)) });
    return data;
  },

  deleteExerciseType: async (id) => {
    await client.delete(`/exercises/types/${id}`);
    set({ types: get().types.filter((t) => t.id !== id) });
  },

  // ── Exercises (заголовки) ──────────────────────────────────────────────────

  fetchExercises: async () => {
    const { data } = await client.get('/exercises/');
    set({ exercises: data });
  },

  /**
   * Создать заголовок упражнения.
   * Payload: { name, description, type_id, complexity_level_id, task }
   * НЕ содержит question/variants/answers — это поля вариантов.
   */
  createExercise: async (payload) => {
    const { data } = await client.post('/exercises/', payload);
    set({ exercises: [...get().exercises, data] });
    return data;
  },

  /**
   * Обновить заголовок упражнения.
   * Payload: { name, description, type_id, complexity_level_id, task }
   */
  updateExercise: async (id, payload) => {
    const { data } = await client.put(`/exercises/${id}`, payload);
    set({ exercises: get().exercises.map((ex) => (ex.id === id ? data : ex)) });
    return data;
  },

  deleteExercise: async (id) => {
    await client.delete(`/exercises/${id}`);
    set({ exercises: get().exercises.filter((ex) => ex.id !== id) });
  },

  setCurrentExercise: (exercise) => set({ current: exercise }),

  // ── Exercise Variants ──────────────────────────────────────────────────────

  /**
   * Получить варианты одного упражнения.
   * Возвращает массив: [{ id, exercise_id, variant, question, variants, answers }]
   */
  fetchVariants: async (exerciseId) => {
    const { data } = await client.get(`/exercises/${exerciseId}/variants`);
    return data;
  },

  /**
   * Создать вариант упражнения.
   * Payload: { variant, question, variants, answers }
   */
  createVariant: async (exerciseId, payload) => {
    const { data } = await client.post(`/exercises/${exerciseId}/variants`, payload);
    return data;
  },

  updateVariant: async (exerciseId, variantId, payload) => {
    const { data } = await client.put(`/exercises/${exerciseId}/variants/${variantId}`, payload);
    return data;
  },

  deleteVariant: async (exerciseId, variantId) => {
    await client.delete(`/exercises/${exerciseId}/variants/${variantId}`);
  },

  // ── Parse Text ─────────────────────────────────────────────────────────────

  /**
   * Разобрать текст и сохранить вариант к упражнению.
   * @param {string} text
   * @param {number} typeId
   * @param {number|null} exerciseId - если передан, вариант сохраняется к упражнению
   * @param {string} [prompt]
   */
  parseText: async (text, typeId, exerciseId, prompt) => {
    const payload = { text, type_id: typeId };
    if (exerciseId) payload.exercise_id = exerciseId;
    if (prompt) payload.prompt = prompt;
    const { data } = await client.post('/exercises/parse-text', payload);
    return data;
  },

  // ── Prompts ────────────────────────────────────────────────────────────────

  fetchPrompts: async () => {
    const { data } = await client.get('/exercises/prompts');
    set({ prompts: data });
    return data;
  },

  createPrompt: async (payload) => {
    const { data } = await client.post('/exercises/prompts', payload);
    set({ prompts: [...get().prompts, data] });
    return data;
  },

  deletePrompt: async (id) => {
    await client.delete(`/exercises/prompts/${id}`);
    set({ prompts: get().prompts.filter((p) => p.id !== id) });
  },

  fetchDefaultPrompt: async () => {
    const { data } = await client.get('/exercises/default-prompt');
    return data.prompt;
  },
}),
    {
      name: 'ewl-exercise-ui',
      partialize: (state) => ({ teacherSelectedTypeId: state.teacherSelectedTypeId }),
    }
  )
);

export default useExerciseStore;
